import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { ProjectTypeDtoIn } from '@/models/in/ProjectTypeDtoIn';
import { useDeleteAuthApi } from '@/hooks/UseApiCalls';

const QUERY_URL = 'management/project-types';

interface DeleteProjectTypeDialogProps {
  open: boolean;
  onClose: () => void;
  projectType: ProjectTypeDtoIn;
  onSuccess: () => void;
}

const DeleteProjectTypeDialog = ({
  open,
  onClose,
  projectType,
  onSuccess,
}: DeleteProjectTypeDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { mutate: archiveType, isPending: isDeleting } = useDeleteAuthApi<void>(
    `${QUERY_URL}/${projectType.id}`
  );

  const handleDelete = () => {
    setError(null);
    archiveType(undefined, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: (err) => {
        console.error('Error archiving project type:', err);
        setError(t('projectTypesManagement.dialog.errorDeleting'));
      },
    });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const confirmMessage = t('projectTypesManagement.confirmDelete', { name: projectType.name });
  const message = error ? `${confirmMessage}\n\n${error}` : confirmMessage;

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={t('projectTypesManagement.delete')}
      message={message}
      confirmText={isDeleting ? 'saving' : 'projectTypesManagement.deleteConfirm'}
      cancelText="projectTypesManagement.deleteCancel"
      onConfirm={handleDelete}
      onCancel={handleClose}
      isLoading={isDeleting}
      variant="danger"
    />
  );
};

export default DeleteProjectTypeDialog;
